import * as d3 from "d3";
import { useRef, useEffect } from "react";
import "./index.css";
import style from "./index.module.scss";

/**
 * @typedef {Object} GraphElements
 * @property {d3.ScaleTime<number, number>} x - D3 time scale for X axis
 * @property {d3.ScaleLinear<number, number>} y - D3 linear scale for Y axis
 * @property {d3.Selection<SVGGElement, unknown, null, undefined>} svg - SVG group element for drawing chart content
 */

const COLORS = {
  NoPPH: "#ff00f5", //"#1E3A8A",
  PerinealTear: "#9B2226",
  AtonicUterus: "#004F2D",
  IncompletePlacenta: "#D97706",
  Combined: "#4C1D95",
};
const ToggleLine_dots = (id, val) => {
  document.querySelector(`#${id}`).style.opacity = val ? 1 : 0;
  document
    .querySelectorAll(`.${id}_dots`)
    .forEach((el) => (el.style.opacity = val ? 1 : 0));
};

const InitialCheckBoxConfig = {
  NoPPH: true,
  PerinealTear: true,
  AtonicUterus: true,
  IncompletePlacenta: true,
  Combined: true,
};
export default function UserPlot({
  mainData,
  width = 640,
  height = 400,
  marginTop = 20,
  marginRight = 20,
  marginBottom = 20,
  marginLeft = 20,
}) {
  const svgRef = useRef(null);
  const createGraph = async () => {
    let dataContainer = {
      NoPPH: {},
      PerinealTear: {},
      AtonicUterus: {},
      IncompletePlacenta: {},
      Combined: {},
    };

    Object.keys(mainData).forEach((entryKey) => {
      let entry = mainData[entryKey];
      // console.log(entry);
      let dateKey = new Date(entry.startTimestamp).toDateString();

      function AddEntry(__dataContainer, __dateKey) {
        if (entry.score === 0) return;

        if (!__dataContainer.hasOwnProperty(__dateKey)) {
          __dataContainer[__dateKey] = []; //0
        }

        __dataContainer[__dateKey].push({
          date: new Date(entry.startTimestamp).toLocaleString(),
          score: entry.score,
          testId: entry.testId,
        });
        // dataContainer[dateKey] += 1//entry.score
      }

      AddEntry(dataContainer["Combined"], dateKey);
      AddEntry(dataContainer[entry.testId], dateKey);
    });

    // append the svg object to the body of the page
    document.querySelector("#plot").innerHTML = "";

    /**
     * Creates a single D3 graph with labeled axes and title on the #plot container.
     *
     * @param {string} graphTitle - The title text to display at the top of the graph.
     * @param {string} xAxisTitle - The label text for the X axis.
     * @param {string} yAxisTitle - The label text for the Y axis.
     * @returns {GraphElements} An object containing the D3 x scale for time, y scale for linear values, and the SVG group selection for drawing.
     */
    function CreateSingleGraph(graphTitle, xAxisTitle, yAxisTitle) {
      // Set the dimensions and margins of the graph
      var margin = { top: 20, right: 20, bottom: 50, left: 70 },
        width = 960 - margin.left - margin.right,
        height = 500 - margin.top - margin.bottom;

      let svgWidth = width + margin.left + margin.right;
      let svgHeight = height + margin.top + margin.bottom;

      /**
       * svg: The root SVG group element used for drawing chart content.
       * @type {d3.Selection<SVGGElement, unknown, null, undefined>}
       */
      var svg = d3
        .select("#plot")
        .append("svg")
        // .attr("width", svgWidth)
        // .attr("height", svgHeight)
        .attr("viewBox", `0 0 ${svgWidth} ${svgHeight}`)
        .attr("preserveAspectRatio", "xMidYMid meet")
        .append("g")
        .attr("transform", `translate(${margin.left}, ${margin.top})`);

      // Add X axis label
      svg
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", width / 2)
        .attr("y", height + margin.bottom - 10)
        .text(xAxisTitle)
        .style("fill", "black")
        .style("font-size", "1.25rem");

      // Add Y axis label
      svg
        .append("text")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(-90)")
        .attr("y", -margin.left + 20)
        .attr("x", -height / 2)
        .text(yAxisTitle)
        .style("fill", "black")
        .style("font-size", "1.25rem");

      // Add Title
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", 0)
        .attr("text-anchor", "middle")
        .style("font-size", "20px")
        .style("font-weight", "bold")
        .style("text-decoration", "underline")
        .text(graphTitle);

      // D3 scale definitions for axes
      /**
       * x: Scale for X axis (time).
       * y: Scale for Y axis (linear).
       */
      var x = d3.scaleTime().range([0, width]);
      var y = d3.scaleLinear().range([height, 0]);

      // Return an object with the scales, SVG group, and dimensions
      return { x, y, svg, width, height };
    }

    const combinedGraph = CreateSingleGraph(
      "Overall Score",
      "Time",
      "Combined"
    );
    const noPPHGraph = CreateSingleGraph("No PPH Score", "Time", "NoPPH");
    const perinealTearGraph = CreateSingleGraph(
      "Perineal Tear Score",
      "Time",
      "PerinealTear"
    );
    const atonicUterusGraph = CreateSingleGraph(
      "Atonic Uterus Score",
      "Time",
      "AtonicUterus"
    );
    const incompletePlacentaGraph = CreateSingleGraph(
      "Incomplete Placenta Score",
      "Time",
      "IncompletePlacenta"
    );

    function DrawLineOnGraph(
      graph,
      id,
      _dataContainer,
      color = "steelblue",
      applyAxisTranform = false,
      showTestId = true
    ) {
      let data = [];
      Object.keys(_dataContainer).forEach((dcKey) => {
        let totalScore = 0;
        let timePlayed = 0;
        _dataContainer[dcKey].forEach((entry) => {
          timePlayed += 1;
          totalScore = Math.max(totalScore, entry.score); //> 0 ? entry.score : 0
        });
        totalScore = totalScore > 0 ? totalScore : 0;
        data.push({
          date: new Date(dcKey),
          value: totalScore, // timePlayed,//totalScore, // < 0 ? 0 : dataContainer[dcKey], //changed to total time played
          odata: _dataContainer[dcKey].sort(
            (x, y) => new Date(x.date).getTime() - new Date(y.date).getTime()
          ),
        });
      });

      data.sort(
        (x, y) => new Date(x.date).getTime() - new Date(y.date).getTime()
      );
      // console.log(data);

      if (applyAxisTranform) {
        graph.x.domain(
          d3.extent(data, (d) => {
            return d.date;
          })
        );
        graph.y.domain([
          0,
          d3.max(data, (d) => {
            return d.value;
          }),
        ]);

        // Get height from the graph object (passed from CreateSingleGraph)
        const graphHeight = graph.height || graph.y.range()[0];

        graph.svg
          .append("g")
          .attr("transform", `translate(0, ${graphHeight})`)
          .call(d3.axisBottom(graph.x));

        graph.svg.append("g").call(d3.axisLeft(graph.y));
      }

      // add the Line
      var valueLine = d3
        .line()
        .x((d) => {
          return graph.x(d.date);
        })
        .y((d) => {
          return graph.y(d.value);
        });

      // svg
      //     .append('defs')
      //     .append('marker')
      //     .attr('id', 'dot')
      //     .attr('viewBox', [0, 0, 10, 10])
      //     .attr('refX', 5)
      //     .attr('refY', 5)
      //     .attr('markerWidth', 5)
      //     .attr('markerHeight', 5)
      //     .append('circle')
      //     .attr('cx', 5)
      //     .attr('cy', 5)
      //     .attr('r', 5)
      //     .style('fill', '#3752f6');

      graph.svg
        .append("path")
        .data([data])
        .attr("id", id)
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", color) // "steelblue")
        // .attr('marker-start', 'url(#dot)')
        // .attr('marker-mid', 'url(#dot)')
        // .attr('marker-end', 'url(#dot)')
        .attr("stroke-width", 1.5)
        .attr("d", valueLine);

      graph.svg
        .selectAll("dot")
        .data(data)
        .enter()
        .append("circle")
        .attr("class", id + "_dots")
        .attr("r", 5)
        .attr("cx", function (d) {
          return graph.x(d.date);
        })
        .attr("cy", function (d) {
          return graph.y(d.value);
        })
        .attr("stroke", color) //"steelblue")
        .attr("stroke-width", 1.5)
        .attr("fill", "#FFFFFF")
        .on("mouseover", function (d, i) {
          console.log(i);
          d3.select(this).transition().duration("100").attr("r", 7);

          //Makes div appear
          div.transition().duration(100).style("opacity", 1);

          let info = "<ol style='margin-right: 1em;'>";
          i.odata.forEach(
            (d) =>
              (info += `<li style='font-weight: ${i.value === d.score ? "bold" : "normal"};color: ${i.value === d.score ? "green" : "white"};'> ${d.date} :: ${d.score} ${
                showTestId ? "[" + d.testId + "]" : ""
              } </li>`)
          );
          info += "</ol>";
          // console.log(info);
          // div.html(d3.format(".2f")(i.value))
          div
            .html(info)
            .style("left", d.pageX + 10 + "px")
            .style("top", d.pageY - 15 + "px");
        })
        .on("mouseout", function (d, i) {
          // console.log(d, i);
          d3.select(this).transition().duration("200").attr("r", 5);

          //makes div disappear
          div.transition().duration("200").style("opacity", 0);
        });
    }
    // console.log(dataContainer);

    DrawLineOnGraph(
      combinedGraph,
      "Combined",
      dataContainer["Combined"],
      COLORS.Combined,
      true,
      true
    );
    if (!InitialCheckBoxConfig.Combined) {
      ToggleLine_dots(`Combined`, false);
    }

    DrawLineOnGraph(
      noPPHGraph,
      "NoPPH",
      dataContainer["NoPPH"],
      COLORS.NoPPH,
      true,
      true
    );
    if (!InitialCheckBoxConfig.NoPPH) {
      ToggleLine_dots(`NoPPH`, false);
    }

    DrawLineOnGraph(
      perinealTearGraph,
      "PerinealTear",
      dataContainer["PerinealTear"],
      COLORS.PerinealTear,
      true,
      true
    );
    if (!InitialCheckBoxConfig.PerinealTear) {
      ToggleLine_dots(`PerinealTear`, false);
    }

    DrawLineOnGraph(
      atonicUterusGraph,
      "AtonicUterus",
      dataContainer["AtonicUterus"],
      COLORS.AtonicUterus,
      true,
      true
    );
    if (!InitialCheckBoxConfig.AtonicUterus) {
      ToggleLine_dots(`AtonicUterus`, false);
    }

    DrawLineOnGraph(
      incompletePlacentaGraph,
      "IncompletePlacenta",
      dataContainer["IncompletePlacenta"],
      COLORS.IncompletePlacenta,
      true,
      true
    );
    if (!InitialCheckBoxConfig.IncompletePlacenta) {
      ToggleLine_dots(`IncompletePlacenta`, false);
    }

    var div = d3
      .select("#plot")
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0);
  };

  useEffect(() => {
    createGraph();
  }, []);

  const HandleCheckbox = (e) => {
    ToggleLine_dots(e.target.name, e.target.checked);
  };

  return (
    <div id="plot" className={style.plotContainer} ref={svgRef}></div>
  );
  return (
    <div
      style={{
        position: "relative",
        width: "fit-content",
      }}
    >
      {false && (
        <div className={style.checkboxContainer}>
          <div>
            <input
              type="checkbox"
              name="Combined"
              onChange={HandleCheckbox}
              defaultChecked={InitialCheckBoxConfig.Combined}
            />
            <label
              style={{
                color: COLORS.Combined,
              }}
            >
              Combined{" "}
            </label>
          </div>

          <div>
            <input
              type="checkbox"
              name="NoPPH"
              onChange={HandleCheckbox}
              defaultChecked={InitialCheckBoxConfig.NoPPH}
            />
            <label
              style={{
                color: COLORS.NoPPH,
              }}
            >
              NoPPH{" "}
            </label>
          </div>

          <div>
            <input
              type="checkbox"
              name="IncompletePlacenta"
              onChange={HandleCheckbox}
              defaultChecked={InitialCheckBoxConfig.IncompletePlacenta}
            />
            <label
              style={{
                color: COLORS.IncompletePlacenta,
              }}
            >
              IncompletePlacenta{" "}
            </label>
          </div>

          <div>
            <input
              type="checkbox"
              name="PerinealTear"
              onChange={HandleCheckbox}
              defaultChecked={InitialCheckBoxConfig.PerinealTear}
            />
            <label
              style={{
                color: COLORS.PerinealTear,
              }}
            >
              PerinealTear{" "}
            </label>
          </div>

          <div>
            <input
              type="checkbox"
              name="AtonicUterus"
              onChange={HandleCheckbox}
              defaultChecked={InitialCheckBoxConfig.AtonicUterus}
            />
            <label
              style={{
                color: COLORS.AtonicUterus,
              }}
            >
              AtonicUterus{" "}
            </label>
          </div>
        </div>
      )}

      <div id="plot" className={style.plotContainer} ref={svgRef}></div>
    </div>
  );
}

{
  /* <p
style={{
  position: "absolute",
  left: "-1.5rem",
  top: "50%",
  fontWeight: "bold",
  transform: "rotate(270deg)translate(50%,0)",
}}
>
Training Count
</p>
<p
style={{
  position: "absolute",
  bottom: "-0.75rem",
  left: "50%",
  fontWeight: "bold",
  marginTop: "1rem",
  transform: "translate(50%, 0px)",
}}
>
Time
</p> */
}

export function User_TotalScore_plot({
  mainData,
  width = 640,
  height = 400,
  marginTop = 20,
  marginRight = 20,
  marginBottom = 20,
  marginLeft = 20,
}) {
  const svgRef = useRef(null);
  const createGraph = async () => {
    let dataContainer = {
      NoPPH: {},
      PerinealTear: {},
      AtonicUterus: {},
      IncompletePlacenta: {},
      Combined: {},
    };

    Object.keys(mainData).forEach((entryKey) => {
      let entry = mainData[entryKey];
      // console.log(entry);
      let dateKey = new Date(entry.startTimestamp).toDateString();

      function AddEntry(__dataContainer, __dateKey) {
        if (!__dataContainer.hasOwnProperty(__dateKey)) {
          __dataContainer[__dateKey] = []; //0
        }
        __dataContainer[__dateKey].push({
          date: new Date(entry.startTimestamp).toLocaleString(),
          score: entry.score,
          testId: entry.testId,
        });
        // dataContainer[dateKey] += 1//entry.score
      }

      AddEntry(dataContainer["Combined"], dateKey);
      AddEntry(dataContainer[entry.testId], dateKey);
    });

    // set the dimensions and margins of the graph
    var margin = { top: 20, right: 20, bottom: 50, left: 70 },
      width = 960 - margin.left - margin.right,
      height = 500 - margin.top - margin.bottom;

    // append the svg object to the body of the page
    document.querySelector("#plot").innerHTML = "";

    /**
     * @type {SVGElement}
     */
    var svg = d3
      .select("#plot")
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // add X axis and Y axis
    var x = d3.scaleTime().range([0, width]);
    var y = d3.scaleLinear().range([height, 0]);

    function DrawLineOnGraph(
      id,
      _dataContainer,
      color = "steelblue",
      applyAxisTranform = false,
      showTestId = true
    ) {
      let data = [];
      Object.keys(_dataContainer).forEach((dcKey) => {
        let totalScore = 0;
        _dataContainer[dcKey].forEach((entry) => {
          totalScore += entry.score; //> 0 ? entry.score : 0
        });
        totalScore = totalScore > 0 ? totalScore : 0;
        data.push({
          date: new Date(dcKey),
          value: totalScore, // < 0 ? 0 : dataContainer[dcKey], //changed to total time played
          odata: _dataContainer[dcKey].sort(
            (x, y) => new Date(x.date).getTime() - new Date(y.date).getTime()
          ),
        });
      });

      data.sort(
        (x, y) => new Date(x.date).getTime() - new Date(y.date).getTime()
      );
      // console.log(data);

      if (applyAxisTranform) {
        x.domain(
          d3.extent(data, (d) => {
            return d.date;
          })
        );
        y.domain([
          0,
          d3.max(data, (d) => {
            return d.value;
          }),
        ]);

        svg
          .append("g")
          .attr("transform", `translate(0, ${height})`)
          .call(d3.axisBottom(x));

        svg.append("g").call(d3.axisLeft(y));
      }

      // add the Line
      var valueLine = d3
        .line()
        .x((d) => {
          return x(d.date);
        })
        .y((d) => {
          return y(d.value);
        });

      // svg
      //     .append('defs')
      //     .append('marker')
      //     .attr('id', 'dot')
      //     .attr('viewBox', [0, 0, 10, 10])
      //     .attr('refX', 5)
      //     .attr('refY', 5)
      //     .attr('markerWidth', 5)
      //     .attr('markerHeight', 5)
      //     .append('circle')
      //     .attr('cx', 5)
      //     .attr('cy', 5)
      //     .attr('r', 5)
      //     .style('fill', '#3752f6');

      svg
        .append("path")
        .data([data])
        .attr("id", id)
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", color) // "steelblue")
        // .attr('marker-start', 'url(#dot)')
        // .attr('marker-mid', 'url(#dot)')
        // .attr('marker-end', 'url(#dot)')
        .attr("stroke-width", 1.5)
        .attr("d", valueLine);

      var path = svg
        .selectAll("dot")
        .data(data)
        .enter()
        .append("circle")
        .attr("class", id + "_dots")
        .attr("r", 5)
        .attr("cx", function (d) {
          return x(d.date);
        })
        .attr("cy", function (d) {
          return y(d.value);
        })
        .attr("stroke", color) //"steelblue")
        .attr("stroke-width", 1.5)
        .attr("fill", "#FFFFFF")
        .on("mouseover", function (d, i) {
          console.log(i);
          d3.select(this).transition().duration("100").attr("r", 7);

          //Makes div appear
          div.transition().duration(100).style("opacity", 1);

          let info = "<ol style='margin-right: 1em;'>";
          i.odata.forEach(
            (d) =>
              (info += `<li> ${d.date} :: ${d.score} ${
                showTestId ? "[" + d.testId + "]" : ""
              } </li>`)
          );
          info += "</ol>";
          // console.log(info);
          // div.html(d3.format(".2f")(i.value))
          div
            .html(info)
            .style("left", d.pageX + 10 + "px")
            .style("top", d.pageY - 15 + "px");
        })
        .on("mouseout", function (d, i) {
          // console.log(d, i);
          d3.select(this).transition().duration("200").attr("r", 5);

          //makes div disappear
          div.transition().duration("200").style("opacity", 0);
        });
    }
    // console.log(dataContainer);
    DrawLineOnGraph(
      "Combined",
      dataContainer["Combined"],
      COLORS.Combined,
      true,
      true
    );
    DrawLineOnGraph("NoPPH", dataContainer["NoPPH"], COLORS.NoPPH);
    ToggleLine_dots(`NoPPH`, false);

    DrawLineOnGraph(
      "PerinealTear",
      dataContainer["PerinealTear"],
      COLORS.PerinealTear
    );
    ToggleLine_dots(`PerinealTear`, false);

    DrawLineOnGraph(
      "AtonicUterus",
      dataContainer["AtonicUterus"],
      COLORS.AtonicUterus
    );
    ToggleLine_dots(`AtonicUterus`, false);

    DrawLineOnGraph(
      "IncompletePlacenta",
      dataContainer["IncompletePlacenta"],
      COLORS.IncompletePlacenta
    );
    ToggleLine_dots(`IncompletePlacenta`, false);

    var div = d3
      .select("#plot")
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0);
  };

  useEffect(() => {
    createGraph();
  }, []);

  const HandleCheckbox = (e) => {
    ToggleLine_dots(e.target.name, e.target.checked);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "fit-content",
      }}
    >
      <div className={style.checkboxContainer}>
        <div>
          <input
            type="checkbox"
            name="Combined"
            onChange={HandleCheckbox}
            defaultChecked
          />
          <label
            style={{
              color: COLORS.Combined,
            }}
          >
            Combined{" "}
          </label>
        </div>

        <div>
          <input type="checkbox" name="NoPPH" onChange={HandleCheckbox} />
          <label
            style={{
              color: COLORS.NoPPH,
            }}
          >
            NoPPH{" "}
          </label>
        </div>

        <div>
          <input
            type="checkbox"
            name="IncompletePlacenta"
            onChange={HandleCheckbox}
          />
          <label
            style={{
              color: COLORS.IncompletePlacenta,
            }}
          >
            IncompletePlacenta{" "}
          </label>
        </div>

        <div>
          <input
            type="checkbox"
            name="PerinealTear"
            onChange={HandleCheckbox}
          />
          <label
            style={{
              color: COLORS.PerinealTear,
            }}
          >
            PerinealTear{" "}
          </label>
        </div>

        <div>
          <input
            type="checkbox"
            name="AtonicUterus"
            onChange={HandleCheckbox}
          />
          <label
            style={{
              color: COLORS.AtonicUterus,
            }}
          >
            AtonicUterus{" "}
          </label>
        </div>
      </div>
      <p
        style={{
          position: "absolute",
          left: "-0.75rem",
          top: "50%",
          fontWeight: "bold",
          transform: "rotate(270deg)translate(50%,0)",
        }}
      >
        Total Score
      </p>
      <p
        style={{
          position: "absolute",
          bottom: "-0.75rem",
          left: "50%",
          fontWeight: "bold",
          marginTop: "1rem",
          transform: "translate(50%, 0px)",
        }}
      >
        Time
      </p>
      <div id="plot" ref={svgRef}></div>
    </div>
  );
}
